<section class="connect-page">
  <div class="connect-card provider-hero">
    <div>
      <span class="connect-pill">Provider</span>
      <h1>{{ providerForm.value.name || 'Provider detail' }}</h1>
        <p class="connect-muted">Edit provider basics, auth, secrets, and utilities.</p>
      <p class="connect-muted">Ref {{ providerForm.value.ref || 'provider.ref' }}</p>
    </div>
    <div class="hero-actions">
      <button mat-stroked-button color="accent" routerLink="/connect/settings">Back to catalog</button>
      <button mat-stroked-button color="warn" (click)="deleteProvider()" [disabled]="isNew || deleting">
        Delete
      </button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || loading">
        Save
      </button>
    </div>
  </div>

  @if (error) {
    <div class="connect-card error-banner">
      <p>{{ error }}</p>
    </div>
  }

  <mat-card class="connect-card">
    <mat-card-content>
      <mat-tab-group>
        <mat-tab [disabled]="true">
          <ng-template mat-tab-label>
            <div class="tab-header-label">
              <span class="category"><small>Provider</small></span>
              <span class="object-name">{{ providerForm.value.name || 'New provider' }}</span>
            </div>
          </ng-template>
        </mat-tab>

        <mat-tab label="Basics">
          <div class="tab-content">
            <form [formGroup]="providerForm" class="provider-form">
              <div class="provider-form__grid">
                <mat-form-field appearance="outline">
                  <mat-label>ID</mat-label>
                  <input matInput formControlName="id" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Name</mat-label>
                  <input matInput formControlName="name" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Ref</mat-label>
                  <input matInput formControlName="ref" placeholder="hubspot.oauth" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Docs URL</mat-label>
                  <input matInput formControlName="docsUrl" placeholder="https://docs.example.com/provider" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Logo URL</mat-label>
                  <input matInput formControlName="logo" placeholder="/assets/providers/hubspot.svg" />
                </mat-form-field>
                <mat-form-field appearance="outline" class="full-row">
                  <mat-label>Description</mat-label>
                  <textarea matInput formControlName="description" rows="2"></textarea>
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Tags (comma separated)</mat-label>
                  <input matInput formControlName="tags" placeholder="crm, sales" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Node color (keyword)</mat-label>
                  <mat-select formControlName="paletteColor">
                    <mat-option value="">Default</mat-option>
                    <mat-option value="blue">Blue</mat-option>
                    <mat-option value="green">Green</mat-option>
                    <mat-option value="orange">Orange</mat-option>
                    <mat-option value="purple">Purple</mat-option>
                    <mat-option value="red">Red</mat-option>
                  </mat-select>
                </mat-form-field>
                <app-color-field
                  inputId="provider-palette-background"
                  label="Background color"
                  hint="Provider node background color."
                  placeholder="#fff6eb"
                  [control]="getPaletteControl('paletteBackground')">
                </app-color-field>
                <app-color-field
                  inputId="provider-palette-border"
                  label="Border color"
                  hint="Provider node border color."
                  placeholder="#fb923c"
                  [control]="getPaletteControl('paletteBorder')">
                </app-color-field>
                <app-color-field
                  inputId="provider-palette-text"
                  label="Text color"
                  hint="Provider node text color."
                  placeholder="#7c2d12"
                  [control]="getPaletteControl('paletteText')">
                </app-color-field>
              </div>
            </form>
          </div>
        </mat-tab>

        <mat-tab label="Auth & OAuth">
          <div class="tab-content">
            <div class="auth-row">
              <div class="auth-options">
                @for (opt of authOptions; track opt) {
                  <mat-checkbox [checked]="authSupported.has(opt)" (change)="toggleAuth(opt, $any($event.checked))">
                    {{ opt }}
                  </mat-checkbox>
                }
              </div>
              <mat-form-field appearance="outline" class="default-auth">
                <mat-label>Default auth</mat-label>
                <mat-select [value]="defaultAuth" (selectionChange)="setDefaultAuth($any($event.value))">
                  @for (opt of authOptions; track opt) {
                    <mat-option [value]="opt" [disabled]="!authSupported.has(opt)">{{ opt }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

            @if (oauthEnabled()) {
              <div class="oauth-grid" [formGroup]="oauthForm">
                <mat-form-field appearance="outline">
                  <mat-label>Authorization URL</mat-label>
                  <input matInput formControlName="authorization_url" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Token URL</mat-label>
                  <input matInput formControlName="token_url" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>App ID (optional)</mat-label>
                  <input matInput formControlName="app_id" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Required scopes (space or comma separated)</mat-label>
                  <input matInput formControlName="required_scopes" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Optional scopes (space or comma separated)</mat-label>
                  <input matInput formControlName="optional_scopes" />
                </mat-form-field>
                <mat-checkbox formControlName="use_pkce">Use PKCE</mat-checkbox>
                <mat-form-field appearance="outline" class="full-row">
                  <mat-label>Extra params (JSON object)</mat-label>
                  <textarea matInput formControlName="extra_params" rows="3"></textarea>
                  <mat-hint>No secrets; these live in the secure fields below.</mat-hint>
                </mat-form-field>
              </div>
            }
          </div>
        </mat-tab>

        <mat-tab label="Secrets">
          <div class="tab-content">
            <div class="secrets">
              <div class="secret-row">
                <div>
                  <p class="secret-label">Client ID</p>
                  <p class="connect-muted">Stored securely; never returned by the API.</p>
                  @if (secretState.client_id.error) {
                    <p class="connect-muted warn">{{ secretState.client_id.error }}</p>
                  }
                </div>
                <div class="secret-inputs">
                  <mat-form-field appearance="outline">
                    <mat-label>Value</mat-label>
                    <input
                      matInput
                      [placeholder]="secretPlaceholder('client_id')"
                      [disabled]="isNew || secretState.client_id.loading"
                      [value]="secretState.client_id.value"
                      (input)="secretState.client_id.value = $any($event.target).value"
                    />
                  </mat-form-field>
                  <button
                    mat-stroked-button
                    color="primary"
                    (click)="saveSecret('client_id')"
                    [disabled]="isNew || secretState.client_id.saving"
                  >
                    Save
                  </button>
                </div>
              </div>

              <div class="secret-row">
                <div>
                  <p class="secret-label">Client secret</p>
                    <p class="connect-muted">Vault-backed; masked by the API.</p>
                  @if (secretState.client_secret.error) {
                    <p class="connect-muted warn">{{ secretState.client_secret.error }}</p>
                  }
                </div>
                <div class="secret-inputs">
                  <mat-form-field appearance="outline">
                    <mat-label>Value</mat-label>
                    <input
                      matInput
                      type="password"
                      [placeholder]="secretPlaceholder('client_secret')"
                      [disabled]="isNew || secretState.client_secret.loading"
                      [value]="secretState.client_secret.value"
                      (input)="secretState.client_secret.value = $any($event.target).value"
                    />
                  </mat-form-field>
                  <button
                    mat-stroked-button
                    color="primary"
                    (click)="saveSecret('client_secret')"
                    [disabled]="isNew || secretState.client_secret.saving"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </mat-tab>

        <mat-tab label="Utilities">
          <div class="tab-content">
            <div class="utility-header">
              <p class="connect-muted">List utilities exposed to UI pickers (select, multiselect, autocomplete).</p>
              <button mat-stroked-button color="primary" (click)="addUtility()">Add utility</button>
            </div>
            @if (utilities.length === 0) {
                <p class="connect-muted">No utilities configured yet. Add one to power utility-backed pickers.</p>
            }
            @for (u of utilities; track u.id; let idx = $index) {
              <div class="utility-block">
                <div class="utility-grid">
                  <mat-form-field appearance="outline">
                    <mat-label>Utility ID</mat-label>
                    <input matInput [value]="u.id" (input)="updateUtility(idx, {id: $any($event.target).value})" />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Name</mat-label>
                    <input matInput [value]="u.name" (input)="updateUtility(idx, {name: $any($event.target).value})" />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Operation</mat-label>
                    <input
                      matInput
                      [value]="u.operation"
                      (input)="updateUtility(idx, {operation: $any($event.target).value})"
                      placeholder="provider.operation"
                    />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Kind</mat-label>
                    <mat-select [value]="u.kind" (selectionChange)="updateUtility(idx, {kind: $any($event.value)})">
                      @for (kind of utilityKinds; track kind) {
                        <mat-option [value]="kind">{{ kind }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Item ID field</mat-label>
                    <input
                      matInput
                      [value]="u.item.idField"
                      (input)="updateUtilityItemField(idx, 'idField', $any($event.target).value)"
                    />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Item label field</mat-label>
                    <input
                      matInput
                      [value]="u.item.labelField"
                      (input)="updateUtilityItemField(idx, 'labelField', $any($event.target).value)"
                    />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Meta fields (comma separated)</mat-label>
                    <input
                      matInput
                      [value]="(u.item.metaFields || []).join(', ')"
                      (input)="updateUtilityMeta(idx, $any($event.target).value)"
                    />
                  </mat-form-field>
                </div>
                <div class="utility-footer">
                  <mat-slide-toggle [checked]="!!u.pagination" (change)="toggleUtilityPagination(idx, $any($event.checked))">
                    Pagination
                  </mat-slide-toggle>
                  <mat-form-field appearance="outline" class="page-size">
                    <mat-label>Page size</mat-label>
                    <input
                      matInput
                      type="number"
                      min="1"
                      [disabled]="!u.pagination"
                      [value]="u.pageSize || ''"
                      (input)="updateUtility(idx, {pageSize: $any($event.target).valueAsNumber})"
                    />
                  </mat-form-field>
                  <span class="spacer"></span>
                  <button mat-button color="warn" (click)="removeUtility(idx)">Remove</button>
                </div>
              </div>
            }
          </div>
        </mat-tab>
      </mat-tab-group>
    </mat-card-content>
    <mat-card-actions class="detail-actions">
      <div class="detail-actions__left">
          <span class="connect-muted small">Edits update the catalog via /api/v1/providers.</span>
      </div>
      <div class="detail-actions__right">
        <button mat-stroked-button color="warn" (click)="deleteProvider()" [disabled]="isNew || deleting">Delete</button>
        <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || loading">Save</button>
      </div>
    </mat-card-actions>
  </mat-card>
</section>
